import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL as API } from '../config/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth state from localStorage on app start
  useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');
      const storedTimestamp = localStorage.getItem('loginTimestamp');
      
      if (storedUser && storedToken) {
        const userObj = JSON.parse(storedUser);
        const expirationMs = userObj.role === 'admin'
          ? 24 * 60 * 60 * 1000 // 1 day for admin
          : 3 * 24 * 60 * 60 * 1000; // 3 days for others
          
        const now = Date.now();
        
        if (storedTimestamp && (now - parseInt(storedTimestamp, 10) > expirationMs)) {
          console.log('Session expired. Logging out.');
          localStorage.clear();
          setUser(null);
          setToken(null);
        } else {
          // If no timestamp exists (legacy session), initialize it now to keep them logged in
          if (!storedTimestamp) {
            localStorage.setItem('loginTimestamp', now.toString());
          }
          setUser(userObj);
          setToken(storedToken);
        }
      }
    } catch (error) {
      console.error('Error loading auth from localStorage:', error);
      localStorage.clear();
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle global 401 Unauthorized errors (token expiration)
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 401) {
          const reqUrl = error.config?.url || '';
          const isAuthAttempt = reqUrl.includes('/auth/password-login') ||
                                reqUrl.includes('/auth/partner-login') ||
                                reqUrl.includes('/auth/verify-otp') ||
                                reqUrl.includes('/auth/send-otp');
          
          if (!isAuthAttempt) {
            console.log('Session expired or invalid token. Logging out.');
            localStorage.clear();
            setUser(null);
            setToken(null);
            
            const currentPath = window.location.pathname;
            if (currentPath.startsWith('/admin')) {
              window.location.href = '/admin/login';
            } else if (currentPath.startsWith('/partner')) {
              window.location.href = '/partner/login';
            } else {
              window.location.href = '/login';
            }
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);


  const login = (userData, tokenData) => {
    setUser(userData);
    setToken(tokenData);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', tokenData);
    localStorage.setItem('loginTimestamp', Date.now().toString());
  };

  const logout = async () => {
    // Show confirmation before logging out
    const confirmLogout = window.confirm('Are you sure you want to logout?');
    if (!confirmLogout) return;

    // Notify backend (best-effort — don't block on failure)
    const currentToken = localStorage.getItem('token');
    if (currentToken) {
      axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${currentToken}` },
      }).catch(() => {});
    }
    setUser(null);
    setToken(null);
    localStorage.clear();
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
